import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Chip, Typography, Box, CircularProgress,
    Tooltip, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getAssignedOrdersRequest, revokeOrderRequest, resetRevokeOrder, resetGetAssignedOrders } from '../../../redux/modules/externalReferrer/slice';
import AssignOrderDialog from './AssignOrderDialog';

const ManageOrdersDialog = ({ open, onClose, externalUser }) => {
    const dispatch = useDispatch();
    const { getAssignedOrdersRes, revokeOrderRes, loading } = useSelector(state => state.externalReferrer);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);

    useEffect(() => {
        if (open && externalUser?._id) {
            dispatch(getAssignedOrdersRequest({ id: externalUser._id, page: 1, limit: 50 }));
        }
        return () => {
            dispatch(resetGetAssignedOrders());
        };
    }, [open, externalUser, dispatch]);

    useEffect(() => {
        if (revokeOrderRes?.success) {
            // Refresh the list after revoking
            dispatch(getAssignedOrdersRequest({ id: externalUser._id, page: 1, limit: 50 }));
            dispatch(resetRevokeOrder());
        }
    }, [revokeOrderRes, externalUser, dispatch]);

    const handleRevoke = (orderId) => {
        if (window.confirm('Are you sure you want to revoke access to this order?')) {
            dispatch(revokeOrderRequest({
                externalReferrerId: externalUser._id,
                orderId: orderId,
                reason: 'Manually revoked by admin'
            }));
        }
    };

    const handleClose = () => {
        dispatch(resetGetAssignedOrders());
        onClose();
    };

    const orders = getAssignedOrdersRes?.data || [];

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    };

    const getAccessLevelLabel = (level) => {
        return level === 'report_and_images' ? 'Report + Images' : 'Report Only';
    };

    const getExpirationStatus = (expiresAt) => {
        if (!expiresAt) return { label: 'Never', color: 'default' };
        const expires = new Date(expiresAt);
        const now = new Date();
        const daysRemaining = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));

        if (daysRemaining <= 0) {
            return { label: 'Expired', color: 'error' };
        } else if (daysRemaining <= 7) {
            return { label: `${daysRemaining}d left`, color: 'warning' };
        }
        return { label: formatDate(expiresAt), color: 'default' };
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                            Manage Orders - {externalUser?.firstName} {externalUser?.lastName}
                        </span>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setAssignDialogOpen(true)}
                        >
                            Assign Order
                        </Button>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : orders.length === 0 ? (
                        <Alert severity="info">
                            No orders have been assigned to this user yet.
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Order ID</TableCell>
                                        <TableCell>Patient</TableCell>
                                        <TableCell>Access Level</TableCell>
                                        <TableCell>Expires</TableCell>
                                        <TableCell>Granted</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {orders.map((access) => {
                                        const expStatus = getExpirationStatus(access.expiresAt);
                                        return (
                                            <TableRow key={access._id} hover>
                                                <TableCell>
                                                    <Typography fontWeight="medium">
                                                        {access.order?.orderId || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {access.order?.patientName || 'Unknown'}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getAccessLevelLabel(access.accessLevel)}
                                                        size="small"
                                                        color={access.accessLevel === 'report_and_images' ? 'primary' : 'default'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={expStatus.label}
                                                        size="small"
                                                        color={expStatus.color}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(access.grantedAt)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Revoke Access">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleRevoke(access.order?._id)}
                                                            disabled={!access.isActive || access.isExpired}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Assign Order Dialog */}
            <AssignOrderDialog
                open={assignDialogOpen}
                onClose={() => {
                    setAssignDialogOpen(false);
                    // Refresh the orders list after assignment
                    if (externalUser?._id) {
                        dispatch(getAssignedOrdersRequest({ id: externalUser._id, page: 1, limit: 50 }));
                    }
                }}
                externalReferrer={externalUser}
            />
        </>
    );
};

export default ManageOrdersDialog;
